import * as React from "react";
import { toast, Modal, ModalFooter } from "@grandvisionhq/common";
import { Util, CheckboxWithLabel } from "@grandvisionhq/forms";
import { useFeatureToggles, useSelector, useDispatch } from "@grandvisionhq/state";
import { ShippingObjectTypes } from "../../../../state.js";
import { updateShippingDetails, toggleDeliveryAddress } from "../../../../StepShipping/StepShipping/actions.js";
import { getBillingAddressFormData, getDeliveryAddressFormData, isDeliveryFormEnabled, getShippingData, getSelectedShippingMethod } from "../../../../StepShipping/StepShipping/selectors.js";
import { useBaseSummarySlot } from "../../../../slots/summaries/slots/summaries/summary-context.js";
import { TitleWithIcon } from "../../../../components/TitleWithIcon/components/TitleWithIcon/TitleWithIcon.js";
import { CheckoutForm } from "../../../../components/Forms/components/Forms/CheckoutForm.js";
import { FormFieldsWithCart } from "../../../../components/FormFieldsWithCart/components/FormFieldsWithCart/FormFieldsWithCart.js";
import { useIsFormSlotAvailable } from "../../../../slots/forms/slots/forms/form-context.js";
import { useCheckoutSettings } from "../../../../context/CheckoutSettings/context/CheckoutSettings/context.js";
import { isDelivery, isDeliveryExpress } from "../../../../utils/utils/shipping-method.js";
const hasAddressData = (addressData) => Boolean(addressData && !Object.values(addressData).every((x) => x === null || x === ""));
const getCountry = (addressData) => addressData && addressData.formState.country?.value || "";
const EditableAddressesSummary = ({ labels }) => {
  const isBillingFormAvailable = useIsFormSlotAvailable(ShippingObjectTypes.BillingAddress);
  const isDeliveryFormAvailable = useIsFormSlotAvailable(ShippingObjectTypes.DeliveryAddress);
  const checkoutSettings = useCheckoutSettings();
  const { allowAlternativeShippingState = false } = useFeatureToggles();
  const billingAddressInitialData = useSelector(getBillingAddressFormData);
  const deliveryAddressInitialData = useSelector(getDeliveryAddressFormData);
  const isDeliveryAddressEnabled = useSelector(isDeliveryFormEnabled);
  const deliveryAddressData = useSelector(getShippingData)?.deliveryAddress;
  const [billingAddressFormData, setBillingAddressFormData] = React.useState(
    () => {
      if (billingAddressInitialData) {
        return {
          formState: billingAddressInitialData,
          formName: ShippingObjectTypes.BillingAddress,
          formValid: true
        };
      }
      return { formState: {}, formName: "", formValid: true };
    }
  );
  const [deliveryAddressFormData, setDeliveryAddressFormData] = React.useState(
    () => {
      if (deliveryAddressInitialData && Boolean(deliveryAddressData)) {
        return {
          formState: deliveryAddressInitialData,
          formName: ShippingObjectTypes.DeliveryAddress,
          formValid: true
        };
      }
      return { formState: {}, formName: "", formValid: true };
    }
  );
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { Slots } = useBaseSummarySlot();
  const dispatch = useDispatch();
  const billingAddressData = useSelector(getShippingData)?.billingAddress;
  const selectedShippingMethod = useSelector(getSelectedShippingMethod)?.method;
  const isDelivery$1 = isDelivery(selectedShippingMethod);
  const isDeliveryExpress$1 = isDeliveryExpress(selectedShippingMethod);
  const [addressesAreEqual, setAddressesAreEqual] = React.useState(
    !Boolean(deliveryAddressData)
  );
  const isDeliveryAddressRequired = React.useMemo(
    () => isDelivery$1 && Boolean(checkoutSettings.shipping?.requiredShippingCountry) && Boolean(getCountry(billingAddressFormData)) && checkoutSettings.shipping?.requiredShippingCountry !== getCountry(billingAddressFormData),
    [billingAddressFormData, checkoutSettings.shipping?.requiredShippingCountry, isDelivery$1]
  );
  React.useEffect(() => {
    setAddressesAreEqual(!Boolean(deliveryAddressData));
  }, [deliveryAddressData]);
  React.useEffect(() => {
    if (isDeliveryAddressRequired && !Boolean(deliveryAddressData)) {
      setAddressesAreEqual(false);
    }
  }, [deliveryAddressData, isDeliveryAddressRequired]);
  const hasBillingAddressData = hasAddressData(billingAddressData);
  const hasDeliveryAddressData = hasAddressData(deliveryAddressData);
  const hasData = hasBillingAddressData || hasDeliveryAddressData;
  const AddressesSummarySlot = Slots.addresses;
  let isPickupOrder = false;
  if (!isDelivery$1 && !isDeliveryExpress$1) {
    isPickupOrder = true;
  }
  if (!AddressesSummarySlot && hasData) {
    console.error(
      "This opco is misconfigured. It has addresses data, but no addresses summary card setup."
    );
  }
  const handleSubmit = React.useCallback(() => {
    if (!billingAddressFormData?.formValid || !addressesAreEqual && !deliveryAddressFormData?.formValid) {
      Util.focusToTopErrorFieldOnScreen();
    } else {
      billingAddressFormData && dispatch(updateShippingDetails({ form: billingAddressFormData }));
      deliveryAddressFormData && dispatch(updateShippingDetails({ form: deliveryAddressFormData }));
      dispatch(toggleDeliveryAddress({ enabled: !addressesAreEqual }));
      setIsModalOpen(false);
    }
  }, [billingAddressFormData, deliveryAddressFormData, addressesAreEqual, dispatch]);
  const handleModalClose = React.useCallback(() => {
    if (!isDeliveryAddressEnabled && !addressesAreEqual) {
      setAddressesAreEqual(true);
    }
    setIsModalOpen(false);
  }, [isDeliveryAddressEnabled, addressesAreEqual]);
  const handleAddressToggle = React.useCallback(() => {
    if (isDeliveryAddressRequired) {
      toast.failed("checkout.address.requiredShippingCountry.errorMessage");
    } else {
      setAddressesAreEqual(!addressesAreEqual);
    }
  }, [addressesAreEqual, isDeliveryAddressRequired]);
  if (!AddressesSummarySlot || !hasData) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Modal,
    {
      isOpen: isModalOpen,
      onModalClose: handleModalClose,
      showIcon: true,
      footer: /* @__PURE__ */ React.createElement(
        ModalFooter,
        {
          ctaCaption: labels?.editModal?.save,
          onCta: handleSubmit,
          onCancel: handleModalClose
        }
      )
    },
    /* @__PURE__ */ React.createElement(TitleWithIcon, { icon: "billing", label: labels.generic.title, "data-t": "billing-label" }),
    isBillingFormAvailable && /* @__PURE__ */ React.createElement(
      CheckoutForm,
      {
        formName: "billingAddress",
        "data-t": "billingAddress",
        initialFormState: billingAddressInitialData,
        onChange: (formData) => setBillingAddressFormData(formData)
      },
      /* @__PURE__ */ React.createElement(FormFieldsWithCart, { formName: ShippingObjectTypes.BillingAddress })
    ),
    !isPickupOrder && /* @__PURE__ */ React.createElement(
      CheckboxWithLabel,
      {
        id: "usingDeliveryAddress",
        value: "usingDeliveryAddress",
        checked: addressesAreEqual,
        onChange: handleAddressToggle,
        className: "billing-and-delivery-form__checkbox",
        label: labels?.differentDeliveryAddress
      }
    ),
    isDeliveryFormAvailable && !addressesAreEqual && !isPickupOrder && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(TitleWithIcon, { icon: "location-pin", label: labels.generic.deliveryAddress }), /* @__PURE__ */ React.createElement(
      CheckoutForm,
      {
        formName: "deliveryAddress",
        "data-t": "deliveryAddress",
        initialFormState: deliveryAddressInitialData,
        onChange: (formData) => setDeliveryAddressFormData(formData)
      },
      /* @__PURE__ */ React.createElement(FormFieldsWithCart, { formName: ShippingObjectTypes.DeliveryAddress })
    ))
  ), hasData && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    AddressesSummarySlot,
    {
      actions: allowAlternativeShippingState ? void 0 : { onEdit: () => setIsModalOpen(true) },
      values: {
        isAddressesEqual: !hasDeliveryAddressData,
        isPickupOrder,
        billingAddress: billingAddressData,
        deliveryAddress: deliveryAddressData
      }
    }
  )));
};
export {
  EditableAddressesSummary,
  EditableAddressesSummary as default
};
//# sourceMappingURL=EditableAddressesSummary.js.map
